import { useState } from 'react';
import { toast } from 'sonner';
import {
  Clock,
  Copy,
  Download,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  FileType,
} from 'lucide-react';
import type { AiMessageFile } from '@/types/ai';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

interface ChatFileAttachmentProps {
  file: AiMessageFile;
}

function isExcelType(fileType: string): boolean {
  const lower = fileType.toLowerCase();
  return (
    lower.includes('sheet') ||
    lower.includes('excel') ||
    lower.includes('csv') ||
    lower === 'xlsx' ||
    lower === 'xls'
  );
}

function isPdfType(fileType: string): boolean {
  return fileType.toLowerCase() === 'pdf' || fileType.toLowerCase().includes('pdf');
}

function getFileThumbnail(fileType: string) {
  if (isExcelType(fileType)) {
    return (
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
        <FileSpreadsheet className="size-5 text-emerald-600" />
      </div>
    );
  }
  if (isPdfType(fileType)) {
    return (
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
        <FileText className="size-5 text-red-600" />
      </div>
    );
  }
  return (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
      <FileType className="size-5 text-violet-600" />
    </div>
  );
}

function getTypeLabel(fileType: string): string {
  return fileType.toUpperCase().replace('APPLICATION/', '').replace('TEXT/', '');
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ChatFileAttachment({ file }: ChatFileAttachmentProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const isPdf =
    file.file_type.toLowerCase().includes('pdf') || file.file_name.toLowerCase().endsWith('.pdf');

  const typeLabel = getTypeLabel(file.file_type);
  const sizeLabel =
    'file_size' in file ? formatFileSize((file as { file_size?: number }).file_size) : '';
  const metaText = [typeLabel, sizeLabel].filter(Boolean).join(' · ');

  // Hint pour le viewer PDF Chromium : cache les thumbnails, ajuste à la largeur.
  const pdfViewerUrl = isPdf ? `${file.file_url}#toolbar=1&navpanes=0&view=FitH` : file.file_url;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(file.file_url);
      toast.success('Lien copié dans le presse-papier');
    } catch {
      toast.error('Impossible de copier le lien');
    }
  };

  const handleOpenChange = (open: boolean) => {
    setPreviewOpen(open);
    if (!open) setPdfLoaded(false);
  };

  return (
    <>
      <div
        role={isPdf ? 'button' : undefined}
        tabIndex={isPdf ? 0 : undefined}
        onClick={isPdf ? () => setPreviewOpen(true) : undefined}
        onKeyDown={
          isPdf
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPreviewOpen(true);
                }
              }
            : undefined
        }
        className="flex items-center gap-3 rounded-xl border border-neutral-200 dark:border-charcoal-700 bg-neutral-50 dark:bg-charcoal-800 px-3 py-2.5 shadow-soft-sm cursor-pointer"
      >
        {getFileThumbnail(file.file_type)}

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-charcoal-800 dark:text-charcoal-200">
            {file.file_name}
          </p>
          {metaText && <p className="mt-0.5 text-[10px] text-charcoal-400">{metaText}</p>}
        </div>

        <a
          href={file.file_url}
          download={file.file_name}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-info/10 text-info hover:bg-info/20 transition-colors"
          aria-label="Télécharger"
        >
          <Download className="size-4" />
        </a>
      </div>

      {isPdf && (
        <Dialog open={previewOpen} onOpenChange={handleOpenChange}>
          <DialogContent className="flex h-[90vh] w-[95vw] max-w-6xl flex-col gap-0 overflow-hidden p-0 sm:w-auto">
            <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b border-neutral-200 dark:border-charcoal-700 px-5 py-4">
              {getFileThumbnail(file.file_type)}
              <div className="min-w-0 flex-1">
                <DialogTitle
                  className="truncate text-base font-semibold"
                  title={file.file_name}
                >
                  {file.file_name}
                </DialogTitle>
                {metaText && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-charcoal-500 dark:text-charcoal-400">
                    <span>{metaText}</span>
                    <span aria-hidden>·</span>
                    <Clock className="size-3" />
                    <span>Lien valide 1h</span>
                  </p>
                )}
              </div>
            </DialogHeader>

            <div className="relative flex-1 overflow-hidden bg-neutral-50 dark:bg-charcoal-900">
              {!pdfLoaded && (
                <div className="absolute inset-0 flex flex-col gap-3 p-6">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-4 w-full max-w-md" />
                  <Skeleton className="h-4 w-full max-w-sm" />
                  <Skeleton className="mt-4 h-full flex-1" />
                </div>
              )}
              <iframe
                title={file.file_name}
                src={pdfViewerUrl}
                onLoad={() => setPdfLoaded(true)}
                className="h-full w-full border-0"
              />
            </div>

            <DialogFooter className="flex-row items-center gap-2 border-t border-neutral-200 dark:border-charcoal-700 px-5 py-3 sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCopyLink}
                className="hidden sm:inline-flex"
              >
                <Copy className="size-3.5" />
                Copier le lien
              </Button>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" asChild>
                  <a href={file.file_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="size-3.5" />
                    <span className="hidden sm:inline">Ouvrir dans un nouvel onglet</span>
                    <span className="sm:hidden">Ouvrir</span>
                  </a>
                </Button>
                <Button type="button" variant="cta" size="sm" asChild>
                  <a
                    href={file.file_url}
                    download={file.file_name}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="size-3.5" />
                    Télécharger
                  </a>
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
